import { useMemo } from "react";
import { Autocomplete, FormControl, Grid, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import CustomNumberInput from "components/CustomNumberInput";
import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import { toDateValue } from "helpers";
import { Article, Client, OrderDraftArticleRow } from "types";

type EditableOrderArticleField = Exclude<keyof OrderDraftArticleRow, "localId">;

type Props = {
  value: OrderDraftArticleRow;
  articles: Article[];
  client: Client | null;
  readOnlyIdentityFields?: boolean;
  onChange: (
    field: EditableOrderArticleField,
    value: string | number | null,
  ) => void;
};

export default function OrderArticleFormFields({
  value,
  articles,
  client,
  readOnlyIdentityFields = false,
  onChange,
}: Props) {
  const articleOptions = useMemo(
    () =>
      articles.map((article) => ({
        id: article.id,
        label: `${article.code} - ${article.description}`,
      })),
    [articles],
  );

  const selectedArticle =
    articles.find((article) => article.id === value.article_id) ?? null;
  const rowUnits =
    (Number(value.amount) || 0) * (Number(value.amount_per_container) || 0);
  const rowWeight =
    (Number(value.amount) || 0) * Number(selectedArticle?.weight ?? 0);

  const clientFieldConfig = getOrderClientFieldConfig(client);
  const showLote = clientFieldConfig.detail.showLote;
  const showOc = clientFieldConfig.detail.showOc;
  const showFechaVencimiento = clientFieldConfig.detail.showFechaVencimiento;
  const trailingFieldsCount =
    1 + Number(showLote) + Number(showOc) + Number(showFechaVencimiento);
  const trailingMd =
    trailingFieldsCount === 1
      ? 12
      : trailingFieldsCount === 2
        ? 6
        : trailingFieldsCount === 3
          ? 4
          : 3;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth margin="none">
          <Autocomplete
            autoComplete
            options={articleOptions}
            disabled={readOnlyIdentityFields}
            value={
              selectedArticle
                ? {
                    id: selectedArticle.id,
                    label: `${selectedArticle.code} - ${selectedArticle.description}`,
                  }
                : null
            }
            isOptionEqualToValue={(option, currentValue) =>
              option.id === currentValue.id
            }
            onChange={(_, selectedValue) =>
              onChange("article_id", selectedValue?.id ?? 0)
            }
            renderInput={(params) => (
              <TextField {...params} required label="Articulo" />
            )}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3}>
        <CustomNumberInput
          label="Cantidad"
          value={value.amount}
          required
          onChange={(event) =>
            onChange("amount", Number(event.target.value || 0))
          }
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <CustomNumberInput
          label="Cantidad por Contenedor"
          value={value.amount_per_container}
          required
          disabled={readOnlyIdentityFields}
          onChange={(event) =>
            onChange("amount_per_container", Number(event.target.value || 0))
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Descripcion"
          value={selectedArticle?.description ?? ""}
          fullWidth
          disabled
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          label="Unidades Totales"
          value={rowUnits}
          fullWidth
          disabled
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          label="Peso"
          value={rowWeight}
          fullWidth
          disabled
        />
      </Grid>

      {showLote && (
        <Grid item xs={12} md={trailingMd}>
          <TextField
            label="Lote"
            value={value.lote}
            disabled={readOnlyIdentityFields}
            onChange={(event) => onChange("lote", event.target.value)}
            fullWidth
          />
        </Grid>
      )}

      {showOc && (
        <Grid item xs={12} md={trailingMd}>
          <TextField
            label="Orden de Compra"
            value={value.oc}
            disabled={readOnlyIdentityFields}
            onChange={(event) => onChange("oc", event.target.value)}
            fullWidth
          />
        </Grid>
      )}

      <Grid item xs={12} md={trailingMd}>
        <DatePicker
          label="Fecha Ingreso"
          format="dd/MM/yyyy"
          disabled={readOnlyIdentityFields}
          value={toDateValue(value.fecha_ingreso)}
          onChange={(dateValue) =>
            onChange(
              "fecha_ingreso",
              dateValue ? dateValue.toISOString().slice(0, 10) : null,
            )
          }
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
      </Grid>

      {showFechaVencimiento && (
        <Grid item xs={12} md={trailingMd}>
          <DatePicker
            label="Fecha de Vencimiento"
            format="dd/MM/yyyy"
            disabled={readOnlyIdentityFields}
            value={toDateValue(value.fecha_vencimiento)}
            onChange={(dateValue) =>
              onChange(
                "fecha_vencimiento",
                dateValue ? dateValue.toISOString().slice(0, 10) : null,
              )
            }
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </Grid>
      )}
    </Grid>
  );
}
